import type { ReactElement } from "react"
import { useContext, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import { Box, Grid, LinearProgress } from "@mui/material"
import { Button, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosResponse } from "axios"
import type { MRT_ColumnDef } from "material-react-table"

import { MonacoTextEditor } from "../../../components/data-display/MonacoTextEditor"
import { Dropzone } from "../../../components/form/Dropzone"
import { BaseTable } from "../../../components/tables/BaseTable"
import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { queryClient } from "../../../store/QueryClientStore"
import type { KonanBasePredictionResponse } from "../../../types/generated/api/KonanBasePredictionResponse"
import { WorkflowInputFeature } from "../../../types/generated/api/WorkflowInputFeature"
import {
  convertCsvToJson,
  generateTableColumnsFromData,
  getEnvVar,
  readFileContentAsync,
} from "../../../utils/genericHelpers"
import { WorkflowCanvasContext } from "../../Workflows"
import { Context } from "../Context"
import type { BulkCreateType } from "../Interfaces"

import styles from "./BulkCreatePredictions.module.scss"

/**
 * BulkCreatePredictions Component
 * Handles bulk creation of predictions by allowing users to upload CSV or JSON files.
 * The component provides file preview functionality and processes predictions in pairs
 * to optimize performance.
 *
 * @param onClose - Function to close the bulk creation modal/view
 */
export function BulkCreatePredictions(props: Readonly<BulkCreateType>): ReactElement {
  const { onClose } = props
  const { id: projectId } = useParams<{ id: string }>()

  // Access workflow and prediction  mutation from context
  const { workflow } = useContext(WorkflowCanvasContext)
  const { sendPredictionMutation, isSendingBulkRequests, setIsSendingBulkRequests } = useContext(Context)

  const { t } = useTranslation("predictions", {
    keyPrefix: "bulkCreatePredictions",
  })

  // Create a lookup object for schema features by their names
  const schema = workflow
    ? Object.groupBy(
        workflow?.active_version.schema.features ?? [],
        (item: { name: string; type?: WorkflowInputFeature.type }) => item.name,
      )
    : {}

  // Get max requests limit from runtime config
  const maxRequestsLimit = Number(getEnvVar("PREDICTIONS_BULK_CREATE_MAX_REQUESTS_LIMIT", 50))
  const maxConcurrentRequests = Number(getEnvVar("PREDICTIONS_BULK_CREATE_CONCURRENT_REQUESTS_LIMIT", 1))

  // State management
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]) // Currently uploaded files
  const [progress, setProgress] = useState<number>(0) // Upload progress
  const [jsonFilePreview, setJSONFilePreview] = useState<string | undefined>(undefined) // JSON preview content
  const [csvFilePreview, setCsvFilePreview] = useState<Array<Record<string, string | null>> | undefined>(undefined) // CSV preview data
  const [csvFileColumns, setCsvFileColumns] = useState<MRT_ColumnDef[]>([]) // CSV table columns

  // Ref to handle request abortion
  const shouldAbortRef = useRef<boolean>(false)

  /**
   * Converts prediction data to the correct types based on the project schema
   *
   * @param predictionBody - The raw prediction data from the uploaded file
   * @returns The prediction data with values converted to their proper types
   */
  const typeCastBasedOnSchemaFeatures = (
    predictionBody: Record<string, PropertyKey | null>,
  ): Record<string, PropertyKey | boolean | null> => {
    const typedPredictionBody: Record<string, PropertyKey | boolean | null> = {}

    for (const [key, value] of Object.entries(predictionBody)) {
      if (!key) continue

      const schemaItem = schema[key]?.[0]
      if (!schemaItem) {
        typedPredictionBody[key] = value
        continue
      }

      if (value === null || value === undefined || value === "") {
        typedPredictionBody[key] = null
        continue
      }

      switch (schemaItem.type) {
        case WorkflowInputFeature.type.NUMBER:
          typedPredictionBody[key] = Number(value)
          break
        case WorkflowInputFeature.type.BOOLEAN:
          // Convert value to string, make it lowercase, and compare with "true"
          // This handles various boolean string representations ("True", "TRUE", "true")
          // Returns true only if the lowercase value exactly matches "true", false otherwise
          typedPredictionBody[key] = String(value).toLowerCase() === "true"
          break
        default:
          typedPredictionBody[key] = value
      }
    }

    return typedPredictionBody
  }

  /**
   * Processes the uploaded file and sends predictions to the backend.
   * Handles both CSV and JSON files, processing them in pairs for better performance.
   * Updates progress and handles errors during the upload process.
   */
  const handleSendPredictions = async (file: File | undefined): Promise<void> => {
    if (!file) return

    const fileContent = await readFileContentAsync(file)

    // Parse file content based on file type
    let content: Array<Record<string, string | null>> = []
    const isCSV = !!file.name.endsWith(".csv")

    if (file.name.endsWith(".json")) content = JSON.parse(fileContent).slice(0, maxRequestsLimit)
    else if (file.name.endsWith(".csv")) content = convertCsvToJson(fileContent).slice(0, maxRequestsLimit)

    setIsSendingBulkRequests?.(true)

    // Array to store pending prediction request promises
    let promises: Array<Promise<AxiosResponse<KonanBasePredictionResponse>> | undefined> = []

    for (let i = 0; i < content.length; i++) {
      // Allow user to cancel ongoing bulk operation
      if (shouldAbortRef.current) {
        setProgress(0)
        break
      }

      // Create and queue a new prediction request
      // For CSV files, type cast the data according to schema
      // For JSON files, use the data as-is
      promises.push(
        sendPredictionMutation?.mutateAsync({
          projectId: projectId as string,
          predictionBody: isCSV ? typeCastBasedOnSchemaFeatures(content[i] ?? {}) : content[i],
        }),
      )

      // Process predictions in batches based on maxConcurrentRequests limit
      // Also process remaining predictions when reaching the end of content
      if (promises.length === maxConcurrentRequests || i === content.length - 1) {
        // Wait for all promises in current batch to complete (success or failure)
        await Promise.allSettled(promises)

        // Update progress bar based on completed predictions
        // Calculate percentage: (completed predictions / total predictions) * 100
        setProgress((prev) => prev + (promises.length / content.length) * 100)

        // Clear promises array for next batch
        promises = []
      }
    }

    // Clean up after completion
    await queryClient.invalidateQueries("project-predictions")
    if (shouldAbortRef.current === false) {
      onClose()
      NotificationUtils.toast(t("notifications.uploadSuccess"), { severity: "success" })
    }
    setIsSendingBulkRequests?.(false)
    shouldAbortRef.current = false
  }

  // TODO: Refactor file handling to use a factory/strategy pattern for different file types.
  /**
   * Handles file acceptance and generates preview data.
   * For JSON files: Shows raw content preview
   * For CSV files: Converts to table format and generates columns
   */
  const handleFileAcceptance = async (file: File): Promise<void> => {
    if (file.name.endsWith(".json")) {
      // Handle JSON preview
      try {
        setJSONFilePreview(await readFileContentAsync(file, 10))
        setAcceptedFiles([file])
      } catch {
        NotificationUtils.toast(t("notifications.invalidJson"), { severity: "error" })
      }
    } else if (file.name.endsWith(".csv")) {
      // Handle CSV preview
      const first10Rows = await readFileContentAsync(file, 10)

      const jsonConvertedRows = convertCsvToJson(first10Rows)

      setCsvFilePreview(jsonConvertedRows)

      // Generate columns from CSV data
      const columns = generateTableColumnsFromData(jsonConvertedRows)

      setCsvFileColumns(columns)
      setAcceptedFiles([file])
    }
  }

  return (
    <Grid item xs={12} className={styles.root}>
      {isSendingBulkRequests ? (
        <Grid container gap={2}>
          <Grid item xs={12} justifyContent={"center"} alignContent={"center"}>
            <KonanEmptyState title={t("emptyState.title")} subTitle={t("emptyState.subtitle")} />
          </Grid>

          <Grid item xs={12} display={"flex"} justifyContent={"center"}>
            <Button variant="dangerous" onClick={() => (shouldAbortRef.current = true)}>
              {t("buttons.abort")}
            </Button>
          </Grid>

          <Box className={styles.linearProgressContainer}>
            <Box className={styles.linearProgress}>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
            <Box minWidth={35}>
              <Typography variant={"label-regular"}>{parseInt(`${progress}`)}%</Typography>
            </Box>
          </Box>
        </Grid>
      ) : (
        <Dropzone
          selectedFiles={acceptedFiles}
          uploadProgress={acceptedFiles.length === 0 ? 0 : 100}
          setSelectedFiles={(files: File[]) => {
            if (files[0]) handleFileAcceptance(files[0])
            else if (files.length === 0) setAcceptedFiles([])
          }}
          bottomText={t("dropzone.bottomText", { limit: maxRequestsLimit })}
          description={t("dropzone.title")}
          titleHeader={{
            title: t("title.text"),
            tooltip: t("title.tooltip"),
          }}
          extensions={{ "application/json": [".json"], "text/*": [".csv"] }}
        />
      )}

      {acceptedFiles[0] && (
        <Grid item className={styles.preview}>
          <Typography variant={"label-bold"} gutterBottom>
            {t("filePreviewTable.title")}
          </Typography>

          <Grid item xs={12}>
            {acceptedFiles[0].name.endsWith(".json") ? (
              <Grid item className={styles.monaco}>
                <MonacoTextEditor readOnly value={jsonFilePreview} language={"json"} />
              </Grid>
            ) : (
              csvFilePreview &&
              csvFileColumns.length !== 0 && (
                <BaseTable
                  data={csvFilePreview}
                  rowCount={csvFilePreview.length}
                  columns={csvFileColumns}
                  disableToolbar
                  title={t("filePreviewTable.baseTable.title")}
                  pageSize={10}
                  isLoading={false}
                  disablePagination
                />
              )
            )}
          </Grid>
        </Grid>
      )}

      <Grid container item className={styles.actions}>
        <Button variant="secondary" disabled={isSendingBulkRequests} onClick={() => onClose()}>
          {t("buttons.cancel")}
        </Button>

        <Button
          variant="primary"
          disabled={acceptedFiles.length === 0 || isSendingBulkRequests}
          onClick={() => handleSendPredictions(acceptedFiles[0])}
        >
          {t("buttons.upload")}
        </Button>
      </Grid>
    </Grid>
  )
}
